import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { format, parseISO, startOfDay, endOfDay, eachMinuteOfInterval, isSameMonth, isToday, startOfMonth, endOfMonth, eachDayOfInterval, differenceInMinutes, areIntervalsOverlapping, isSameDay, startOfWeek, addDays, isAfter, isBefore, addMinutes } from 'date-fns';
import { ChevronLeftIcon, ChevronRightIcon, XMarkIcon, ClockIcon, MapPinIcon, UserIcon, ChevronDoubleLeftIcon, ChevronDoubleRightIcon } from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  setSelectedDate, 
  setLoadingShared, 
  setSharedEvents, 
  setSharedEventsError,
  fetchShareRequests,
  updateEvent,
  deleteEvent,
  shareEvent
} from '../../../state/calendarSlice';
import { 
  FaClock, 
  FaMapMarkerAlt, 
  FaInfoCircle, 
  FaPencilAlt, 
  FaTrash, 
  FaBell, 
  FaShare, 
  FaTimes, 
  FaCheck, 
  FaUserFriends 
} from 'react-icons/fa';
import EventDetailsDialog from './EventDetailsDialog';
import NewEventForm from './NewEventForm';
import ShareEventDialog from './ShareEventDialog';
import calendarService from '../../../services/calendarService';

const eventColors = ['#FBBC04', '#4CCEAC', '#A0AEC0', '#FF0000', '#FFFFFF'];

// Define a flag to disable notification features - must match the one in other files
const NOTIFICATIONS_ENABLED = false; // Set to false to disable notifications

export default function DayView({ showSharedEvents = true }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const selectedDateString = useSelector((state) => state.calendar.selectedDate);
  const selectedDate = useMemo(() => new Date(selectedDateString), [selectedDateString]);
  const user = useSelector((state) => state.auth.user);
  const [events, setEvents] = useState([]);
  const sharedEvents = useSelector((state) => state.calendar.sharedEvents || []);
  const scrollContainerRef = useRef(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const timeLabelsRef = useRef(null);
  const mainContentRef = useRef(null);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const token = useSelector((state) => state.auth.token);
  const isLoadingShared = useSelector((state) => state.calendar.isLoadingShared);
  const sharedEventsError = useSelector((state) => state.calendar.sharedEventsError);
  const pendingShareRequests = useSelector((state) => 
    state.calendar.shareRequests?.filter(req => 
      req.status === 'pending' && req.recipientId === user?.id
    ) || []
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loadingShared, setLoadingSharedState] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [editingEvent, setEditingEvent] = useState(null);
  const [isEditFormOpen, setIsEditFormOpen] = useState(false);
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [eventToShare, setEventToShare] = useState(null);

  const fetchEvents = useCallback(async () => {
    if (!user?.id) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Format the date to YYYY-MM-DD
      const localDate = format(selectedDate, 'yyyy-MM-dd');
      
      // Use calendarService instead of direct fetch
      const response = await calendarService.fetchEvents(localDate, user.id);
      
      // Extract events array from the response
      const eventsArray = response.events || [];
      
      setEvents(eventsArray);
    } catch (error) {
      setError('Failed to load events. Please try again.');
      setEvents([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  }, [selectedDate, user]);

  const fetchUpcomingEvents = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      const startDate = format(startOfDay(selectedDate), 'yyyy-MM-dd');
      const endDate = format(addDays(endOfDay(selectedDate), 7), 'yyyy-MM-dd');
      
      // Use calendarService directly
      const response = await calendarService.fetchEventsByRange(startDate, endDate, user.id);
      
      // Extract events array from the response
      const fetchedEvents = response.events || [];
      
      const sortedUpcomingEvents = fetchedEvents
        .filter(event => isAfter(parseISO(event.startTime), new Date()))
        .sort((a, b) => parseISO(a.startTime) - parseISO(b.startTime))
        .slice(0, 5); // Get the next 5 upcoming events
      
      setUpcomingEvents(sortedUpcomingEvents);
    } catch (error) {
      setUpcomingEvents([]); // Set empty array on error
    }
  }, [selectedDate, user]);

  // Add fetchSharedEvents function for explicit shared events fetching
  const fetchSharedEvents = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      dispatch(setLoadingShared(true));
      
      // Use calendarService to fetch shared events
      const response = await calendarService.fetchSharedEvents(user.id);
      
      // Extract events array from the response
      const sharedEventsData = response.events || [];
      
      // Dispatch the shared events to the Redux store
      dispatch(setSharedEvents(sharedEventsData));
    } catch (error) {
      dispatch(setSharedEventsError('Failed to load shared events'));
    } finally {
      dispatch(setLoadingShared(false));
    }
  }, [user, dispatch]);



  useEffect(() => {
    if (user?.id) {
      fetchEvents();
    }
  }, [fetchEvents, user?.id]);

  useEffect(() => {
    if (user?.id) {
      fetchUpcomingEvents();
    }
  }, [fetchUpcomingEvents, user?.id]);
  
  // Add effect to fetch shared events
  useEffect(() => {
    if (user?.id) {
      fetchSharedEvents();
    }
  }, [fetchSharedEvents, user?.id]);

  // Special check for March 20 event - debug only
  useEffect(() => {
    if (user?.id && 
        user.id === '5642e274-b081-70e5-5fc7-799746fc09c2' && 
        format(selectedDate, 'yyyy-MM-dd') === '2025-03-20') {
      const debugEvent = {
        id: "780fa571-6771-4c9c-9dd0-03dc951adbc0",
        title: "Birthday Party",
        description: "Birthday party",
        startTime: "2025-03-20T12:00:00.000Z",
        endTime: "2025-03-20T16:00:00.000Z",
        duration: 240,
        location: "Hall",
        color: "#3B82F6",
        userId: "5642e274-b081-70e5-5fc7-799746fc09c2",
        sharedWith: ["94edf0e2-985c-4cb6-a3b3-0df8ecd69c21"]
      };
      
      // Check if this specific event is already in our events list
      const eventExists = events.some(e => e.id === debugEvent.id);
      if (!eventExists) {
        setEvents(prev => [...prev, debugEvent]);
      }
    }
  }, [user?.id, selectedDate, events]);

  const timeSlots = eachMinuteOfInterval({
    start: startOfDay(selectedDate),
    end: endOfDay(selectedDate)
  }, { step: 30 });

  const monthStart = startOfMonth(selectedDate);
  const monthEnd = endOfMonth(selectedDate);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 }); // Start on Monday
  const calendarEnd = addDays(monthEnd, 7 - (monthEnd.getDay() || 7) + 1); // Ensure we end on a Sunday
  const daysInMonth = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  useEffect(() => {
    if (mainContentRef.current && events.length > 0) {
      const firstEventTime = events.reduce((earliest, event) => {
        const startTime = parseISO(event.startTime);
        return startTime < earliest ? startTime : earliest;
      }, parseISO(events[0].startTime));

      const scrollPosition = (firstEventTime.getHours() * 60 + firstEventTime.getMinutes()) * 2 - 120;
      mainContentRef.current.scrollTop = Math.max(0, scrollPosition);
    }
  }, [events]);

  useEffect(() => {
    const timeLabelsContainer = timeLabelsRef.current;
    const mainContentContainer = mainContentRef.current;

    if (timeLabelsContainer && mainContentContainer) {
      const syncScroll = () => {
        timeLabelsContainer.scrollTop = mainContentContainer.scrollTop;
      };

      mainContentContainer.addEventListener('scroll', syncScroll);

      return () => {
        mainContentContainer.removeEventListener('scroll', syncScroll);
      };
    }
  }, []);

  const handleDateClick = (date) => {
    dispatch(setSelectedDate({ selectedDate: date.toString() }));
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  };

  const getOverlappingEvents = (event, allEvents) => {
    return allEvents.filter(otherEvent => 
      otherEvent.id !== event.id && 
      areIntervalsOverlapping(
        { start: parseISO(event.startTime), end: parseISO(event.endTime) },
        { start: parseISO(otherEvent.startTime), end: parseISO(otherEvent.endTime) }
      )
    );
  };

  // Improve the filteredSharedEvents function to strictly filter by the selectedDate
  const filteredSharedEvents = useMemo(() => {
    return sharedEvents.filter(event => {
      try {
        // Use startTime or fall back to start if needed
        const startTime = event.startTime || event.start;
        if (!startTime) return false;
        
        const eventDate = parseISO(startTime);
        const selectedDateStart = startOfDay(selectedDate);
        const selectedDateEnd = endOfDay(selectedDate);
        
        // Only include events that occur ON the selected date
        return eventDate >= selectedDateStart && eventDate <= selectedDateEnd;
      } catch (error) {
        return false;
      }
    });
  }, [sharedEvents, selectedDate]);
  
  // Modify the allEvents useMemo to properly filter events based on showSharedEvents
  const allEvents = useMemo(() => {
    if (!user?.id) return [];
    
    // Get all eventIds that are pending acceptance
    const pendingEventIds = pendingShareRequests.map(req => req.eventId);
    
    // First determine which events are owned by the current user vs shared with them
    const ownedEvents = [];
    const receivedSharedEvents = [];
    
    // Process events from regular events array
    events.forEach(event => {
      // Check if this event is owned by current user or shared with them
      if (event.userId === user.id) {
        // This is user's own event
        ownedEvents.push({...event});
      } else {
        // This event belongs to someone else but shared with current user
        // Only add if showSharedEvents is true and it's not a pending request
        if (showSharedEvents && !pendingEventIds.includes(event.id)) {
          receivedSharedEvents.push({...event, isShared: true});
        }
      }
    });
    
    // Process events from shared events array if toggle is on
    if (showSharedEvents) {
      sharedEvents.forEach(event => {
        // Only add if it's not user's own event, not already added, and not pending
        if (event.userId !== user.id && 
            !pendingEventIds.includes(event.id) && 
            !ownedEvents.some(e => e.id === event.id) && 
            !receivedSharedEvents.some(e => e.id === event.id)) {
          receivedSharedEvents.push({...event, isShared: true});
        }
      });
    }
    
    // Combine the lists - owned events are always shown, shared events only when toggle is on
    return [...ownedEvents, ...(showSharedEvents ? receivedSharedEvents : [])];
  }, [events, sharedEvents, showSharedEvents, pendingShareRequests, user]);

  const renderEvents = () => {
    // First ensure we only have events for the current day
    const filteredEvents = allEvents.filter(event => {
      try {
        const eventStart = event.startTime || event.start;
        if (!eventStart) return false;
        
        const eventDate = parseISO(eventStart);
        const dayStart = startOfDay(selectedDate);
        const dayEnd = endOfDay(selectedDate);
        
        return eventDate >= dayStart && eventDate <= dayEnd;
      } catch (err) {
        return false;
      }
    });
    
    // Then sort events by start time
    const sortedEvents = filteredEvents.sort((a, b) => {
      // Use startTime or fall back to start
      const aTime = a.startTime || a.start;
      const bTime = b.startTime || b.start;
      if (!aTime || !bTime) return 0;
      
      return parseISO(aTime).getTime() - parseISO(bTime).getTime();
    });
    
    // Group overlapping events
    const groupedEvents = [];
    sortedEvents.forEach(event => {
      // Ensure event has startTime and endTime
      const eventStart = event.startTime || event.start;
      const eventEnd = event.endTime || event.end;
      
      if (!eventStart || !eventEnd) {
        return;
      }
      
      const overlappingGroup = groupedEvents.find(group => 
        group.some(groupEvent => {
          const groupEventStart = groupEvent.startTime || groupEvent.start;
          const groupEventEnd = groupEvent.endTime || groupEvent.end;
          
          if (!groupEventStart || !groupEventEnd) return false;
          
          return areIntervalsOverlapping(
            { start: parseISO(eventStart), end: parseISO(eventEnd) },
            { start: parseISO(groupEventStart), end: parseISO(groupEventEnd) }
          );
        })
      );
      
      if (overlappingGroup) {
        overlappingGroup.push(event);
      } else {
        groupedEvents.push([event]);
      }
    });

    return groupedEvents;
  };

  const handleEditEvent = async (event) => {
    try {
      console.log('Preparing to edit event:', event);
      
      // Make sure the event has all required fields
      if (!event.id || !user.id) {
        console.error('Missing event ID or user ID for edit');
        return;
      }
      
      // Set the event to be edited and open the edit form
      setEditingEvent(event);
      setIsEditFormOpen(true);
      
      // Close the event details dialog
      setSelectedEvent(null);
    } catch (error) {
      console.error('Error preparing to edit event:', error);
    }
  };

  const handleDeleteEvent = async (event) => {
    try {
      console.log('Deleting event:', event);
      // Make sure the event has an ID before dispatching
      if (!event.id || !user.id) {
        console.error('Missing event ID or user ID for delete');
        return;
      }
      
      await dispatch(deleteEvent({ eventId: event.id, userId: user.id }));
      
      // Refresh events after deletion
      fetchEvents();
      
      // Close the dialog
      setSelectedEvent(null);
    } catch (error) {
      console.error('Error deleting event:', error);
    }
  };

  const handleShareEvent = async (event) => {
    try {
      console.log('Sharing event:', event);
      if (!event.id) {
        console.error('Missing event ID for share');
        return;
      }
      
      // Open the share dialog
      setEventToShare(event);
      setIsShareDialogOpen(true);
    } catch (error) {
      console.error('Error sharing event:', error);
      setFeedback('Failed to share event. Please try again.');
      setTimeout(() => setFeedback(''), 3000);
    }
  };

  const handleSetReminder = (event) => {
    // Show reminder options for users
    alert('This feature is not yet implemented.');
  };

  // Add a cleanup of events when date changes
  useEffect(() => {
    // Clear events when date changes to prevent stale data
    setEvents([]);
    
    if (user?.id) {
      fetchEvents();
    }
  }, [fetchEvents, user?.id, selectedDate]);

  // Debug allEvents after combining


  return (
    <div className="flex flex-col h-full bg-gradient-to-br from-pri-dark via-[#1a1c2e] to-sec-dark text-pri-white">
      <div className="flex flex-1 overflow-hidden relative">
        <div className="flex-1 flex overflow-hidden">
          {/* Time Labels Column */}
          <div className="w-16 sm:w-20 flex-shrink-0 border-r border-pri-white/10 bg-gradient-to-b from-sec-dark/80 to-pri-dark/80 backdrop-blur-xl">
            <div className="h-full overflow-y-scroll hide-scrollbar" ref={timeLabelsRef}>
              {timeSlots.map((slot, index) => (
                <motion.div 
                  key={index} 
                  className="h-[60px] relative flex items-start justify-end pr-2"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.01 }}
                >
                  {index % 2 === 0 && (
                    <div className="text-xs sm:text-sm text-pri-gray font-medium">
                      {format(slot, 'h:mm a')}
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>

          {/* Main Calendar Grid */}
          <div className="flex-1 overflow-hidden relative">
            {/* Glossy Background */}
            <div className="absolute inset-0 bg-gradient-to-b from-sec-dark/80 to-pri-dark/80 backdrop-blur-xl">
              <div className="absolute inset-0 bg-gradient-to-br from-pri-yellow/5 via-transparent to-pri-green/5" />
              <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-pri-white/5 to-transparent" />
            </div>

            {/* Calendar Content */}
            <div className="h-full overflow-y-scroll hide-scrollbar relative" ref={mainContentRef}>
              <div className="relative" style={{ height: `${timeSlots.length * 60}px` }}>
                {/* Time Slots Grid */}
                {timeSlots.map((slot, index) => (
                  <motion.div 
                    key={index}
                    className={`absolute w-full ${
                      index % 2 === 0 
                        ? 'border-b border-dashed border-pri-white/5' 
                        : 'border-b border-pri-white/10'
                    }`}
                    style={{ top: `${index * 60}px`, height: '60px' }}
                    initial={{ opacity: 0, scaleY: 0 }}
                    animate={{ opacity: 1, scaleY: 1 }}
                    transition={{ duration: 0.5, delay: index * 0.01 }}
                  />
                ))}

                {/* Events */}
                {Array.isArray(renderEvents()) && renderEvents().map((group, groupIndex) => (
                  <React.Fragment key={`group-${groupIndex}`}>
                    {Array.isArray(group) && group.map((event) => {
                      // Use startTime or start for consistency
                      const eventStartTime = event.startTime || event.start;
                      const eventEndTime = event.endTime || event.end;
                      
                      if (!eventStartTime || !eventEndTime) {
                        return null;
                      }
                      
                      const startMinutes = differenceInMinutes(
                        parseISO(eventStartTime),
                        startOfDay(selectedDate)
                      );
                      const duration = differenceInMinutes(
                        parseISO(eventEndTime),
                        parseISO(eventStartTime)
                      );
                      
                      const startPercentage = (startMinutes / 1440) * 100;
                      const heightPercentage = (duration / 1440) * 100;

                      // Group overlapping events
                      const overlappingEvents = allEvents.filter(otherEvent => {
                        const otherStart = otherEvent.startTime || otherEvent.start;
                        const otherEnd = otherEvent.endTime || otherEvent.end;
                        
                        if (!otherStart || !otherEnd) return false;
                        
                        return areIntervalsOverlapping(
                          { start: parseISO(eventStartTime), end: parseISO(eventEndTime) },
                          { start: parseISO(otherStart), end: parseISO(otherEnd) }
                        );
                      });

                      const eventIndex = overlappingEvents.indexOf(event);
                      const width = 90 / overlappingEvents.length;
                      const left = (eventIndex * width) + 5;

                      // Add visual indicator for shared events
                      const isSharedEvent = event.userId !== user?.id || event.isShared;
                      const isSelected = selectedEvent && selectedEvent.id === event.id;

                      return (
                        <motion.div
                          key={`event-${event.id}`}
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          whileHover={{ 
                            scale: 1.02,
                            zIndex: 10,
                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)'
                          }}
                          onClick={() => {
                            // Mark the event as shared before passing to dialog
                            const eventWithShareInfo = {
                              ...event,
                              isShared: isSharedEvent
                            };
                            setSelectedEvent(eventWithShareInfo);
                          }}
                          className={`absolute rounded-xl overflow-hidden cursor-pointer transition-all duration-300 ${
                            isSharedEvent ? 'border-2 border-pri-yellow/50' : ''
                          }`}
                          style={{
                            top: `${startPercentage}%`,
                            height: `${heightPercentage}%`,
                            left: `${left}%`,
                            width: `${width - 2}%`,
                            minHeight: '80px',
                            backgroundColor: event.color || 'rgba(255, 192, 203, 0.8)', // Soft pink background
                            zIndex: isSelected ? 10 : 1,
                            // Add a subtle pattern for shared events
                            backgroundImage: isSharedEvent ? 'linear-gradient(45deg, rgba(255,255,255,0.1) 25%, transparent 25%, transparent 50%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.1) 75%, transparent 75%, transparent)' : 'none',
                            backgroundSize: isSharedEvent ? '10px 10px' : 'auto'
                          }}
                        >
                          <div className="h-full relative group">
                            {/* Glossy Background with Gradient - use event color instead of fixed gradient */}
                            <div className="absolute inset-0">
                              <div 
                                className="absolute inset-0 opacity-90" 
                                style={{ background: event.color || 'linear-gradient(to right, var(--soft-pink), var(--mint-green))' }} 
                              />
                              <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-transparent" />
                              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent" />
                              <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-black/10 to-transparent" />
                            </div>
                            
                            {/* Event Content */}
                            <div className="relative p-3 sm:p-4 h-full flex flex-col backdrop-blur-sm">
                              <div className="flex-1">
                                <h3 className="font-semibold text-pri-dark text-sm sm:text-base truncate mb-2 drop-shadow-sm">
                                  {event.title}
                                </h3>
                                <div className="space-y-1.5">
                                  <div className="flex items-center text-xs sm:text-sm text-pri-dark/80">
                                    <FaClock className="mr-2 h-3 w-3" />
                                    <span>{format(parseISO(eventStartTime), 'h:mm a')} - {format(parseISO(eventEndTime), 'h:mm a')}</span>
                                  </div>
                                  {event.location && (
                                    <div className="flex items-center text-xs sm:text-sm text-pri-dark/80">
                                      <FaMapMarkerAlt className="mr-2 h-3 w-3" />
                                      <span className="truncate">{event.location}</span>
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Duration Badge */}
                              <div className="absolute bottom-2 right-2 text-xs bg-pri-dark/10 px-2 py-0.5 rounded-full text-pri-dark/80 backdrop-blur-sm
                                            opacity-0 group-hover:opacity-100 transition-opacity duration-300 border border-pri-dark/10">
                                {formatDuration(duration)}
                              </div>
                            </div>

                            {/* Hover Effect Overlay */}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/5 via-transparent to-white/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                          </div>
                        </motion.div>
                      );
                    })}
                  </React.Fragment>
                ))}

                {/* Current Time Indicator */}
                {isSameDay(selectedDate, new Date()) && (
                  <motion.div
                    initial={{ opacity: 0, scaleX: 0 }}
                    animate={{ opacity: 1, scaleX: 1 }}
                    className="absolute left-0 right-0 flex items-center z-10"
                    style={{
                      top: `${(new Date().getHours() * 60 + new Date().getMinutes()) * (100 / 1440)}%`,
                    }}
                  >
                    <div className="w-2 h-2 rounded-full bg-pri-yellow shadow-lg shadow-pri-yellow/50" />
                    <div className="flex-1 border-t border-dashed border-pri-yellow" />
                  </motion.div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Use the new EventDetailsDialog component */}
      {selectedEvent && (
        <EventDetailsDialog 
          event={selectedEvent} 
          onClose={() => setSelectedEvent(null)}
          onDelete={handleDeleteEvent}
          onEdit={handleEditEvent}
          onShare={handleShareEvent}
          onSetReminder={handleSetReminder}
          isShared={selectedEvent.userId !== user?.id || selectedEvent.isShared || false}
        />
      )}

      {/* Add the NewEventForm for editing events */}
      <NewEventForm
        isOpen={isEditFormOpen}
        onClose={() => {
          setIsEditFormOpen(false);
          setEditingEvent(null);
        }}
        onEventAdded={(updatedEvent) => {
          console.log('Event updated:', updatedEvent);
          fetchEvents(); // Refresh events after update
        }}
        initialEvent={editingEvent} // Pass the event to be edited
        isEditMode={true}
      />

      {/* Add the ShareEventDialog component */}
      <ShareEventDialog
        isOpen={isShareDialogOpen}
        onClose={() => setIsShareDialogOpen(false)}
        event={eventToShare}
        onEventShared={() => {
          setFeedback('Event shared successfully!');
          setTimeout(() => setFeedback(''), 3000);
          fetchEvents(); // Refresh events to get the updated sharing status
        }}
      />
    </div>
  );
}

// Keep the InputField component at the end of the file
function InputField({ label, type, value, onChange, required = false }) {
  return (
    <div className="mb-4">
      <label htmlFor={label.toLowerCase()} className="block text-sm font-medium text-pri-white mb-1">
        {label}
      </label>
      {type === 'textarea' ? (
        <textarea
          id={label.toLowerCase()}
          name={label.toLowerCase()}
          rows={3}
          className="w-full px-3 py-2 text-pri-dark bg-white bg-opacity-90 rounded-md border border-gray-300 focus:border-indigo-500 focus:ring focus:ring-indigo-500 focus:ring-opacity-50 transition duration-150 ease-in-out"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <input
          type={type}
          name={label.toLowerCase()}
          id={label.toLowerCase()}
          required={required}
          className="w-full px-3 py-2 text-pri-dark bg-white bg-opacity-90 rounded-md border border-gray-300 focus:border-indigo-500 focus:ring focus:ring-indigo-500 focus:ring-opacity-50 transition duration-150 ease-in-out"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </div>
  );
}
